import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import cn from 'classnames';
import ConfigContext from '../../../contexts/ConfigContext';
import usePageContent from '../../../hooks/usePageContent';

// Components
import LogoList from '../content/LogoList';
import Meta from '../../shared/Meta';
import CtaImage from '../content/CtaImage';
import FeaturedTrucks from '../FeaturedTrucks';
import WhyBookWithUs from '../content/WhyBookWithUs';
import Triple from '../content/Triple';
import CostEstimator from '../content/CostEstimator';
import FeaturedCuisines from '../FeaturedCuisines';
import CateringFaq from '../faqs/CateringFaq';
import CateringCta from '../content/CateringCta';
import AnyEvent from '../content/AnyEvent';
import Testimonials from '../content/Testimonials';

import styles from '../../../../stylesheets/public/layouts/AboutPage.module.postcss';
import tripleStyles from '../content/Triple.module.postcss';
import CDN_BASE, { cdnPhoto } from '../../../utils/cdn';

const featuredTrucks = { featured: true };

// Catering hub. Copy follows the voice bible §20 (design/voice-bible-2026-09-v4.1.md):
// hero → why food trucks work → how it works (canonical three steps) → pricing/trust.
function Catering() {
  const { site_title, vehicle_type, city_name } = useContext(ConfigContext);
  const vehicleType = vehicle_type.toLowerCase();
  const citySlug = city_name.toLowerCase();

  // CMS-driven copy for the catering page. `cms` is null if no published row
  // exists for this city → all references fall back to the hardcoded JSX.
  const { content: cms } = usePageContent('catering');

  return (
    <div className='CateringPage'>
      <Meta
        title={`Food ${vehicleType} catering in ${city_name}`}
        type='website'
        canonical='/catering'
        image={cdnPhoto(`${citySlug}-food-truck-catering`)}
        description={`Food ${vehicleType} catering for ${city_name} events. Tell us what you're planning and compare quotes from local ${vehicleType}s. Requesting quotes is free.`}
        structuredData={{
          cateringService: {
            name: `Food ${vehicle_type} Catering Services in ${city_name}`,
            description: `Food ${vehicleType} catering in ${city_name} for corporate events, weddings, parties, and festivals.`,
            url: '/catering',
            serviceType: 'Food Truck Catering',
          },
          breadcrumbs: {
            listItems: [
              { '@type': 'ListItem', position: 2, name: 'Catering', item: '/catering' }
            ]
          }
        }}
      />

      <div className='Section'>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image={`${CDN_BASE}/photos/${citySlug}-food-truck-catering.webp`} alt='Food truck catering at an event' noLazy isResponsive />
          <div className={styles.Cta_copy}>
            <h1>{cms?.hero?.headline || `Food ${vehicleType} catering for ${city_name} events`}</h1>
            {cms?.hero?.body ? (
              <p>{cms.hero.body}</p>
            ) : (
              <p>Tell us what you&#39;re planning. We&#39;ll find {vehicleType}s that fit your date, location, guest count, and budget, whether it&#39;s a <Link to='/food-truck-catering/corporate-catering'>corporate event</Link>, a <Link to='/food-truck-catering/wedding-catering'>wedding</Link>, a <Link to='/food-truck-catering/birthday-parties'>birthday party</Link>, or a <Link to='/food-truck-catering/community-events'>festival</Link>.</p>
            )}
            <p>
              <Link to='/catering/requests/new' className='Button Button--mobile-w100'>{cms?.hero?.cta_label || 'Get catering quotes'}</Link>
            </p>
            <p className='u-mb0'>Requesting quotes is free. Before you book, you&#39;ll see the full price, including platform fees.</p>
          </div>
        </div>
      </div>

      <section className='Section'>
        <LogoList title={`Companies that book food ${vehicleType}s with us`} titleClassName='u-textCenter' className='u-mt1' />
      </section>

      <FeaturedTrucks
        title={`Featured food ${vehicleType}s`}
        options={featuredTrucks}
      />

      <WhyBookWithUs />

      <section className='Section--extraPaddingTop'>
        <h2 className='u-textCenter u-mb3'>What you get with {site_title}</h2>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image={`${CDN_BASE}/photos/${citySlug}-food-truck-catering-service.webp`} alt='A lineup of local food trucks' isResponsive />
          <div className={styles.Cta_copy}>
            <h3>{cms?.differentiators?.vendors_heading || `${vehicle_type}s across ${city_name}`}</h3>
            {cms?.differentiators?.vendors_body ? (
              <p>{cms.differentiators.vendors_body}</p>
            ) : (
              <p><Link to='/food-truck-catering/taco-catering'>Tacos</Link>, <Link to='/food-truck-catering/bbq-catering'>BBQ</Link>, <Link to='/food-truck-catering/pizza-catering'>pizza</Link>, <Link to='/food-truck-catering/burger-catering'>burgers</Link>, <Link to='/food-truck-catering/dessert-catering'>desserts</Link>, and plenty more. Browse menus and photos before you decide.</p>
            )}
          </div>
        </div>
      </section>

      <section className='Section'>
        <div className={styles.Cta}>
          <CtaImage image={`${CDN_BASE}/photos/${citySlug}-food-truck-catering-setup.webp`} alt='Comparing food truck quotes' isResponsive />
          <div className={styles.Cta_copy}>
            <h3>{cms?.differentiators?.booking_heading || 'One request, real quotes'}</h3>
            {cms?.differentiators?.booking_body ? (
              <p>{cms.differentiators.booking_body}</p>
            ) : (
              <p>Send one request. Available {vehicleType}s reply with packages and pricing, and you can message them directly before you choose. Your event details stay editable while you <Link to='/food-truck-catering/prices'>compare</Link>.</p>
            )}
          </div>
        </div>
      </section>

      <section className='Section'>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image={`${CDN_BASE}/photos/${citySlug}-food-truck-catering-buffet.webp`} alt='Food truck catering buffet' isResponsive />
          <div className={styles.Cta_copy}>
            <h3>{cms?.differentiators?.more_heading || 'Help when you need it'}</h3>
            {cms?.differentiators?.more_body ? (
              <p>{cms.differentiators.more_body}</p>
            ) : (
              <p>Our team reviews every request before it goes out and can help with changes to your guest count, date, or lineup, whether it&#39;s an <Link to='/food-truck-catering/office-lunch'>office lunch</Link>, a <Link to='/food-truck-catering/wedding-catering'>wedding reception</Link>, a <Link to='/food-truck-catering/private-parties'>private party</Link>, or a <Link to='/food-truck-catering/fundraiser'>fundraiser</Link>.</p>
            )}
          </div>
        </div>
      </section>

      <section className='Section--extraPadding'>
        <h3 className='u-phosphate'>How it works</h3>
        <div className={tripleStyles.Triple_wrapper}>
          <Triple
            image={`${CDN_BASE}/photos/${citySlug}-howitworks1.webp`}
            isResponsive
            alt='Step 1: tell us about the event'
            title='1. Tell us about the event'
            text='Date, location, guest count, and what you know so far.'
          />
          <Triple
            image={`${CDN_BASE}/photos/${citySlug}-howitworks2.webp`}
            isResponsive
            alt='Step 2: compare your options'
            title='2. Compare your options'
            text={`Review available ${vehicleType}s, menus, pricing, and messages.`}
          />
          <Triple
            image={`${CDN_BASE}/photos/${citySlug}-howitworks3.webp`}
            isResponsive
            alt='Step 3: book the one that fits'
            title='3. Book the one that fits'
            text={`Choose your ${vehicleType} and confirm the details.`}
          />
        </div>
      </section>

      <AnyEvent />

      <Testimonials title='What customers say' />

      <CostEstimator />

      <section className='Section--extraPadding'>
        <FeaturedCuisines />
      </section>

      <section className='Section--extraPadding'>
        <CateringFaq />
      </section>

      <section className='Section--extraPadding'>
        <CateringCta title='Ready to find a truck?' />
      </section>

    </div>
  );
}

export default Catering;
